import {
  MAX_AFILHADOS_LIMIT,
  adminStreamerSchema,
  padrinhoSummarySchema,
  type AdminStreamer,
  type PadrinhoSummary,
} from '@growup/streaming-shared';
import {
  Button,
  Card,
  DateInput,
  Field,
  Input,
  Notice,
  Select,
  StatusBadge,
  Table,
  todayInLisbon,
} from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

import { getJson, messageOf, send } from '../api';
import { formatDate } from '../format';

const loadPadrinhos = () => getJson('/api/streaming/padrinhos', z.array(padrinhoSummarySchema));

/** "joao (2 livres)", for the pickers. */
export function padrinhoOption(p: PadrinhoSummary): string {
  if (p.free === 0) return `${p.login} (cheio)`;
  return `${p.login} (${String(p.free)} ${p.free === 1 ? 'livre' : 'livres'})`;
}

type Group = 'none' | 'provisional' | 'confirmed';

const GROUPS: { id: Group; title: string; empty: string }[] = [
  { id: 'none', title: 'Sem padrinho', empty: 'Todos os streamers têm padrinho.' },
  {
    id: 'provisional',
    title: 'Provisórios (confirmar na reunião mensal)',
    empty: 'Nenhum padrinho provisório.',
  },
  { id: 'confirmed', title: 'Confirmados', empty: 'Ainda não há padrinhos confirmados.' },
];

const groupOf = (s: AdminStreamer): Group =>
  s.padrinho === null ? 'none' : s.padrinho.confirmed ? 'confirmed' : 'provisional';

/**
 * Padrinhos e afilhados (GS1), for the Stream Staff and the monthly meeting:
 * who has free places, who has no padrinho, who is still provisional. Each
 * streamer is assigned, confirmed or changed one by one; changing sends an
 * email to the streamer and the new padrinho.
 */
export function PadrinhosView({ onChanged }: { onChanged?: () => void } = {}) {
  const [padrinhos, setPadrinhos] = useState<PadrinhoSummary[] | null>(null);
  const [streamers, setStreamers] = useState<AdminStreamer[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    Promise.all([
      loadPadrinhos(),
      getJson('/api/streaming/streamers', z.array(adminStreamerSchema)),
    ])
      .then(([p, s]) => {
        setPadrinhos(p);
        setStreamers(s.filter((x) => x.status !== 'removed'));
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);
  useEffect(load, [load]);

  const replaced = (s: AdminStreamer) => {
    setStreamers((list) => list?.map((x) => (x.id === s.id ? s : x)) ?? null);
    // A troca muda a ocupacao dos padrinhos.
    loadPadrinhos()
      .then(setPadrinhos)
      .catch(() => undefined);
    onChanged?.();
  };

  const without = streamers?.filter((s) => s.padrinho === null).length ?? 0;
  const provisional = streamers?.filter((s) => groupOf(s) === 'provisional').length ?? 0;
  const requests = streamers?.filter((s) => s.padrinhoRequest !== null).length ?? 0;

  return (
    <div className="flex flex-col gap-4">
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {streamers !== null && (without > 0 || provisional > 0 || requests > 0) ? (
        <Notice tone={without > 0 || requests > 0 ? 'warning' : 'info'}>
          <span aria-label="Resumo dos padrinhos">
            {without === 1
              ? '1 streamer sem padrinho'
              : `${String(without)} streamers sem padrinho`}
            {` · ${String(provisional)} ${provisional === 1 ? 'provisório' : 'provisórios'} por confirmar`}
            {requests > 0
              ? ` · ${String(requests)} ${requests === 1 ? 'pedido' : 'pedidos'} de outro padrinho`
              : ''}
          </span>
        </Notice>
      ) : null}
      <PadrinhoTable padrinhos={padrinhos} onSaved={load} />
      {GROUPS.map((g) => {
        const rows = streamers?.filter((s) => groupOf(s) === g.id) ?? [];
        return (
          <Card key={g.id} headingLevel={2} title={`${g.title} · ${String(rows.length)}`}>
            {streamers === null ? <p className="text-gu-ink-muted">A carregar…</p> : null}
            {streamers !== null && rows.length === 0 ? (
              <p className="text-gu-ink-muted">{g.empty}</p>
            ) : null}
            <ul className="flex flex-col divide-y divide-gu-line">
              {rows.map((s) => (
                <AssignRow key={s.id} streamer={s} padrinhos={padrinhos ?? []} onSaved={replaced} />
              ))}
            </ul>
          </Card>
        );
      })}
    </div>
  );
}

function PadrinhoTable({
  padrinhos,
  onSaved,
}: {
  padrinhos: PadrinhoSummary[] | null;
  onSaved: () => void;
}) {
  return (
    <Card
      headingLevel={2}
      title="Padrinhos e madrinhas"
      eyebrow="Quem tem o papel Padrinho/Madrinha em Staff. O máximo combina-se com cada um."
    >
      <Table
        caption="Padrinhos e madrinhas"
        columns={[
          { key: 'login', header: 'Padrinho', cell: (p) => p.login },
          {
            key: 'afilhados',
            header: 'Afilhados',
            cell: (p) => `${String(p.afilhados)} de ${String(p.maxAfilhados)}`,
          },
          {
            key: 'free',
            header: 'Livres',
            cell: (p) =>
              p.free === 0 ? <StatusBadge tone="warning">Cheio</StatusBadge> : String(p.free),
          },
          {
            key: 'alerts',
            header: 'Semáforo',
            cell: (p) =>
              p.noContact === 0 && p.overdueReports === 0 ? (
                <StatusBadge tone="success">Em dia</StatusBadge>
              ) : (
                <span className="inline-flex flex-wrap gap-1">
                  {p.noContact > 0 ? (
                    <StatusBadge tone="danger">{p.noContact} sem contacto</StatusBadge>
                  ) : null}
                  {p.overdueReports > 0 ? (
                    <StatusBadge tone="warning">
                      {p.overdueReports}{' '}
                      {p.overdueReports === 1 ? 'relatório em atraso' : 'relatórios em atraso'}
                    </StatusBadge>
                  ) : null}
                </span>
              ),
          },
          {
            key: 'max',
            header: 'Máximo',
            align: 'right',
            cell: (p) => <MaxEditor padrinho={p} onSaved={onSaved} />,
          },
        ]}
        rows={padrinhos ?? []}
        rowKey={(p) => p.accountId}
        empty={
          padrinhos === null
            ? 'A carregar…'
            : 'Ainda ninguém tem o papel Padrinho/Madrinha. Dê-o em Staff.'
        }
      />
    </Card>
  );
}

function MaxEditor({ padrinho, onSaved }: { padrinho: PadrinhoSummary; onSaved: () => void }) {
  const [value, setValue] = useState(String(padrinho.maxAfilhados));
  const [error, setError] = useState<string | null>(null);
  const n = Number(value);
  const valid = value !== '' && Number.isInteger(n) && n >= 0 && n <= MAX_AFILHADOS_LIMIT;

  const save = async () => {
    setError(null);
    try {
      await send('PUT', `/api/streaming/padrinhos/${padrinho.accountId}`, { maxAfilhados: n });
      onSaved();
    } catch (e) {
      setError(messageOf(e));
    }
  };

  return (
    <div className="flex items-center justify-end gap-2">
      <Input
        type="number"
        min={0}
        max={MAX_AFILHADOS_LIMIT}
        className="w-20"
        aria-label={`Máximo de afilhados de ${padrinho.login}`}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <Button
        size="sm"
        variant="secondary"
        disabled={!valid || n === padrinho.maxAfilhados}
        onClick={() => void save()}
      >
        Guardar
      </Button>
      {error ? <span className="text-caption text-gu-danger">{error}</span> : null}
    </div>
  );
}

function AssignRow({
  streamer: s,
  padrinhos,
  onSaved,
}: {
  streamer: AdminStreamer;
  padrinhos: PadrinhoSummary[];
  onSaved: (s: AdminStreamer) => void;
}) {
  const [choice, setChoice] = useState(s.padrinho?.accountId ?? '');
  const [introOn, setIntroOn] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (path: string, method: 'PUT' | 'POST', body: unknown) => {
    setBusy(true);
    setError(null);
    try {
      onSaved(adminStreamerSchema.parse(await send(method, path, body)));
      setIntroOn('');
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  const assign = (confirmed: boolean) =>
    run(`/api/streaming/streamers/${s.id}/padrinho`, 'PUT', {
      padrinhoId: choice === '' ? null : choice,
      confirmed,
    });

  const same = choice === (s.padrinho?.accountId ?? '');
  // Quem esta cheio so aparece se ja for o padrinho deste streamer.
  const options = padrinhos.filter((p) => p.free > 0 || p.accountId === s.padrinho?.accountId);

  return (
    <li className="flex flex-col gap-2 py-3" aria-label={`Padrinho de ${s.displayName}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-semibold">
          {s.displayName}{' '}
          <span className="text-caption font-normal text-gu-ink-muted">twitch.tv/{s.channel}</span>
        </span>
        <span className="text-caption text-gu-ink-muted">
          {s.padrinho
            ? `${s.padrinho.confirmed ? 'Padrinho' : 'Padrinho provisório'}: ${s.padrinho.login} desde ${formatDate(s.padrinho.since)}`
            : 'Sem padrinho'}
          {' · '}
          {s.intro
            ? `Conversa de apresentação a ${formatDate(`${s.intro.on}T12:00:00Z`)} com ${s.intro.by}`
            : 'Conversa de apresentação por fazer'}
        </span>
      </div>
      {s.padrinhoRequest ? (
        <p role="note" className="text-caption font-semibold text-gu-danger">
          Pediu outro padrinho a {formatDate(s.padrinhoRequest.at)}: «{s.padrinhoRequest.reason}».
          Atribua outro (ou confirme o mesmo) para fechar o pedido.
        </p>
      ) : null}
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Padrinho" className="w-60">
          <Select value={choice} onChange={(e) => setChoice(e.target.value)}>
            <option value="">Sem padrinho</option>
            {options.map((p) => (
              <option key={p.accountId} value={p.accountId}>
                {padrinhoOption(p)}
              </option>
            ))}
          </Select>
        </Field>
        {choice !== '' && (!same || !s.padrinho?.confirmed) ? (
          <Button size="sm" className="mb-1.5" disabled={busy} onClick={() => void assign(true)}>
            {same ? 'Confirmar' : 'Atribuir e confirmar'}
          </Button>
        ) : null}
        {choice !== '' && !same ? (
          <Button
            size="sm"
            variant="secondary"
            className="mb-1.5"
            disabled={busy}
            onClick={() => void assign(false)}
          >
            Atribuir provisório
          </Button>
        ) : null}
        {choice === '' && s.padrinho !== null ? (
          <Button
            size="sm"
            variant="secondary"
            className="mb-1.5"
            disabled={busy}
            onClick={() => void assign(false)}
          >
            Tirar padrinho
          </Button>
        ) : null}
        {s.intro === null ? (
          <>
            <Field label="Conversa de apresentação" optional className="w-48">
              <DateInput value={introOn} onChange={setIntroOn} max={todayInLisbon()} />
            </Field>
            <Button
              size="sm"
              variant="secondary"
              className="mb-1.5"
              disabled={busy || introOn === ''}
              onClick={() =>
                void run(`/api/streaming/streamers/${s.id}/intro`, 'POST', { on: introOn })
              }
            >
              Registar (eu estive)
            </Button>
          </>
        ) : null}
      </div>
      {error ? <Notice tone="danger">{error}</Notice> : null}
    </li>
  );
}
